import React, { useEffect, useRef, useState } from 'react'
import HttpClient from '../../../services/HttpClient'

export default function MissionInviteAutocomplete({
  territorySlug,
  missionId,
  excludeCharacterId,
  onInvite,
  disabled = false,
}) {
  const [query, setQuery] = useState('')
  const [selected, setSelected] = useState(null)
  const [suggestions, setSuggestions] = useState([])
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const debounceRef = useRef(null)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (selected) return undefined

    const q = query.trim()
    if (q.length < 2) {
      setSuggestions([])
      setOpen(false)
      return undefined
    }

    clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(async () => {
      setLoading(true)
      try {
        const { data } = await HttpClient().get('/api/v1/missions/invite-candidates', {
          params: {
            territorySlug,
            missionId,
            q,
            excludeCharacterId,
          },
        })
        const rows = Array.isArray(data) ? data : []
        setSuggestions(rows)
        setOpen(rows.length > 0)
      } catch {
        setSuggestions([])
        setOpen(false)
      } finally {
        setLoading(false)
      }
    }, 250)

    return () => clearTimeout(debounceRef.current)
  }, [query, territorySlug, missionId, excludeCharacterId, selected])

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const pick = (candidate) => {
    setSelected(candidate)
    setQuery(candidate.name)
    setOpen(false)
  }

  const handleSubmit = () => {
    if (!selected?.characterId) return
    onInvite(selected)
    setSelected(null)
    setQuery('')
    setSuggestions([])
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      if (open && suggestions.length === 1 && !selected) {
        pick(suggestions[0])
        return
      }
      handleSubmit()
    }
    if (event.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <div className="mission-invite-autocomplete" ref={wrapRef}>
      <div className="mission-card__invite-row">
        <div className="mission-invite-autocomplete__field">
          <input
            className="field-input"
            type="text"
            value={query}
            onChange={(event) => {
              setSelected(null)
              setQuery(event.target.value)
            }}
            onFocus={() => {
              if (suggestions.length > 0) setOpen(true)
            }}
            onKeyDown={handleKeyDown}
            placeholder="Nombre del shinobi"
            disabled={disabled}
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
          />
          {loading && <span className="mission-invite-autocomplete__hint">Buscando…</span>}
          {open && suggestions.length > 0 && (
            <ul className="mission-invite-autocomplete__list" role="listbox">
              {suggestions.map((candidate) => (
                <li key={candidate.characterId} role="option">
                  <button type="button" onClick={() => pick(candidate)}>
                    {candidate.name}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <button
          type="button"
          className="mission-btn"
          onClick={handleSubmit}
          disabled={disabled || !selected}
        >
          Invitar
        </button>
      </div>
    </div>
  )
}
